import { AxiosProgressEvent } from 'axios';
import {
  ChatRequest,
  CreateBidRequest,
  GetBidsListRequest,
  GetDocumentsListRequest,
  UpdateBidRequest,
} from '../types/Dtos/request';
import { CreateBidResponse, GetBidDetailsResponse, GetBidsResponse } from '../types/Dtos/response';
import { api, getQueryParamsFromObj } from '../utils/api';
import { aiApi } from '../utils/aiApi';
import { ProxyEvaluationTypeEnum } from '../utils/constants';
import {
  deleteDocuments,
  getIndexingStatus,
  listDocuments,
  runAnalysis,
  uploadSingleDocument,
} from './documentsService';
import { connectToChatStream } from './chatService';

const BASE = 'v1/bids';

export const getBidDetails = (bidId: string) =>
  aiApi.get<GetBidDetailsResponse>(`${BASE}/${bidId}`);

export const getBids = ({ tenderPackageId, ...pagination }: GetBidsListRequest) =>
  aiApi.get<GetBidsResponse>(
    `${BASE}?${getQueryParamsFromObj({ tenderId: tenderPackageId, ...pagination })}`,
  );

export const createBid = (payload: CreateBidRequest, tenderPackageId: string) =>
  aiApi.post<CreateBidResponse>(
    `${BASE}?${getQueryParamsFromObj({ tenderId: tenderPackageId })}`,
    payload,
  );

export const uploadBidDocument = (
  file: File,
  bidId: string,
  onUploadProgress: (progressEvent: AxiosProgressEvent) => void,
) => uploadSingleDocument(file, ProxyEvaluationTypeEnum.bid, bidId, undefined, onUploadProgress);

export const analyseBid = (bidId: string) => runAnalysis(ProxyEvaluationTypeEnum.bid, bidId);

export const connectToBidChatStream = ({
  id,
  conversationId,
  userPrompt,
  specifiedDocuments,
  clarificationResponse,
}: ChatRequest) => {
  const body = {
    chat_session_id: `${conversationId}`,
    eval_id: id,
    eval_type: ProxyEvaluationTypeEnum.bid,
    query: userPrompt,
    specified_documents: specifiedDocuments,
    clarification_response: undefined,
  };
  if (clarificationResponse) body.clarification_response = clarificationResponse;
  return connectToChatStream(id, ProxyEvaluationTypeEnum.bid, body);
};

export const exportBidReport = (bidId: string) =>
  api.get<Blob>(`bids/${bidId}/overview/export`, {
    responseType: 'blob',
  });

export const deleteBidDocuments = (bidId: string, documentsIds: string[]) =>
  deleteDocuments(ProxyEvaluationTypeEnum.bid, bidId, documentsIds);

export const markBidStatementAsStarred = (bidId: string, statementId: string) =>
  aiApi.patch(`${BASE}/${bidId}/statements/${statementId}/star`);

export const unmarkBidStatementAsStarred = (bidId: string, statementId: string) =>
  aiApi.patch(`${BASE}/${bidId}/statements/${statementId}/unstar`);

export const getBidDocuments = ({
  id,
  orderBy,
  orderDirection,
  searchText,
}: GetDocumentsListRequest) =>
  listDocuments(ProxyEvaluationTypeEnum.bid, id, orderBy, orderDirection, searchText);

export const getProxyBidIndexedDocumentsStatus = (bidId: string) =>
  getIndexingStatus(ProxyEvaluationTypeEnum.bid, bidId);

export const deleteBid = (bidId: string) => aiApi.delete(`${BASE}/${bidId}`);

export const updateBid = (bidId: string, data: UpdateBidRequest) =>
  aiApi.put(`${BASE}/${bidId}`, data);
